import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { format } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { adminDeleteDemo, adminListDemos, adminUpdateDemoStatus } from '../api/demosApi';
import './demos/Demos.css';

const CENTRAL_TIMEZONE = 'America/Chicago';
const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Get timezone abbreviation (EST, CST, PST, etc.)
const getTimezoneAbbr = () => {
  const options = { timeZoneName: 'short' };
  const timeStr = new Date().toLocaleTimeString('en-US', options);
  return timeStr.split(' ')[2];
};

const Demos = () => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDemos, setFilteredDemos] = useState([]);
  const { admin } = useAdminAuth(); // Get admin context

  useEffect(() => {
    if (admin?.uid) {
      fetchDemos();
    }
  }, [admin?.uid]);

  useEffect(() => {
    filterDemos();
  }, [searchTerm, demos]);

  useEffect(() => {
    // UX: when the query changes, jump back to first page
    setPage(0);
  }, [searchTerm]);

  const fetchDemos = async () => {
    try {
      setLoading(true);
      const demosData = (await adminListDemos()).map((demo) => ({
        ...demo,
        dateTime: demo.dateTime ? new Date(demo.dateTime) : null,
      }));
      setDemos(demosData);
      setError(null);
    } catch (err) {
      console.error('Error fetching demos:', err);
      setError('Failed to load demo bookings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterDemos = () => {
    const filtered = demos.filter(demo => {
      const searchString = searchTerm.toLowerCase();
      return (
        demo.name?.toLowerCase().includes(searchString) ||
        demo.email?.toLowerCase().includes(searchString) ||
        demo.companyName?.toLowerCase().includes(searchString)
      );
    });
    setFilteredDemos(filtered);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (next) => {
    setRowsPerPage(parseInt(next, 10));
    setPage(0);
  };

  const handleStatusChange = async (demoId, newStatus) => {
    try {
      await adminUpdateDemoStatus({ demoId, status: newStatus });
      setDemos((prevDemos) =>
        prevDemos.map((demo) => (demo.id === demoId ? { ...demo, status: newStatus } : demo))
      );
    } catch (err) {
      console.error('Error updating demo status:', err);
      setError('Failed to update demo status');
    }
  };

  const handleDeleteDemo = async (demoId) => {
    if (!window.confirm('Are you sure you want to delete this demo booking?')) {
      return;
    }

    try {
      // Use admin functions to delete demo
      await adminDeleteDemo({ demoId });
      
      // Update local state
      setDemos(prevDemos => prevDemos.filter(demo => demo.id !== demoId));
    } catch (err) {
      console.error('Error deleting demo:', err);
      setError('Failed to delete demo booking');
    }
  };

  const getStatusChipProps = (status) => {
    const statusConfig = {
      scheduled: { color: 'primary', label: 'Scheduled' },
      completed: { color: 'success', label: 'Completed' },
      cancelled: { color: 'error', label: 'Cancelled' },
      'no-show': { color: 'warning', label: 'No Show' }
    };
    return statusConfig[status] || { color: 'default', label: status };
  };

  const getStatusClass = (status) => {
    const safe = String(status || '').toLowerCase().replace(/\s+/g, '-');
    return `demoStatus demoStatus--${safe || 'unknown'}`;
  };

  const formatDemoTime = (dateTime) => {
    // First convert UTC to Central (company default/source of truth)
    const centralTime = utcToZonedTime(dateTime, CENTRAL_TIMEZONE);
    // Then convert from Central to admin's local timezone
    const localTime = utcToZonedTime(zonedTimeToUtc(centralTime, CENTRAL_TIMEZONE), LOCAL_TIMEZONE);
    
    return {
      date: format(localTime, 'MMM d, yyyy'),
      time: format(localTime, 'h:mm a') + ` ${getTimezoneAbbr()}`
    };
  };

  const total = filteredDemos.length;
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, total);
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = page > 0;
  const canNext = endIndex < total;

  const rowsToRender = filteredDemos.slice(startIndex, endIndex);

  const onPrev = () => setPage((p) => Math.max(0, p - 1));
  const onNext = () => setPage((p) => Math.min(pageCount - 1, p + 1));

  const timezoneAbbr = getTimezoneAbbr();

  if (loading) {
    return (
      <div className="admin-page demosPage">
        <div className="admin-page__header">
          <div>
            <h1 className="admin-page__title">Demo Bookings</h1>
            <p className="admin-page__subtitle">Review and track scheduled demos, completions and no-shows.</p>
          </div>
          <div className="demosHeaderRight">
            <span className="demosPill">Timezone: {timezoneAbbr}</span>
          </div>
        </div>

        <div className="demosCard">
          <div className="demosSearchRow">
            <div className="demosSearch">
              <label>Search</label>
              <div className="demosSkeletonBar" style={{ width: 420, height: 38, borderRadius: 12 }} />
            </div>
            <div className="demosSkeletonBar" style={{ width: 140, height: 34, borderRadius: 999 }} />
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table demosTable" aria-label="Demo bookings table">
            <thead>
              <tr>
                <th>Date &amp; Time ({timezoneAbbr})</th>
                <th>Name</th>
                <th>Contact</th>
                <th>Company</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }).map((_, idx) => (
                <tr key={idx}>
                  <td><div className="demosSkeletonBar" /></td>
                  <td><div className="demosSkeletonBar" style={{ width: 140 }} /></td>
                  <td><div className="demosSkeletonBar" style={{ width: 220 }} /></td>
                  <td><div className="demosSkeletonBar" style={{ width: 160 }} /></td>
                  <td><div className="demosSkeletonBar" style={{ width: 110 }} /></td>
                  <td style={{ textAlign: 'right' }}><div className="demosSkeletonBar" style={{ width: 180 }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page demosPage">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Demo Bookings</h1>
          <p className="admin-page__subtitle">Review and track scheduled demos, completions and no-shows.</p>
        </div>
        <div className="demosHeaderRight">
          <span className="demosPill">Timezone: {timezoneAbbr}</span>
          <span className="demosPill">{total} total</span>
        </div>
      </div>

      {error ? (
        <div className="alert alertError" role="alert" aria-live="polite" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      <div className="demosCard" aria-label="Search demos">
        <div className="demosSearchRow">
          <div className="demosSearch">
            <label htmlFor="demoSearch">Search</label>
            <input
              id="demoSearch"
              type="search"
              placeholder="Search by name, email, or company…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table demosTable" aria-label="Demo bookings table">
          <thead>
            <tr>
              <th>Date &amp; Time ({timezoneAbbr})</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Company</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rowsToRender.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="demosEmpty">
                    No demos found{searchTerm ? ' for this search.' : '.'}
                  </div>
                </td>
              </tr>
            ) : (
              rowsToRender.map((demo) => {
                const when = demo.dateTime ? formatDemoTime(demo.dateTime) : null;
                const status = getStatusChipProps(demo.status);

                return (
                  <tr key={demo.id}>
                    <td>
                      <div className="demosTwoLine">
                        <span className="demosPrimaryText">{when?.date || '—'}</span>
                        <span className="demosMutedText">{when?.time || '—'}</span>
                      </div>
                    </td>
                    <td>
                      <span className="demosPrimaryText">{demo.name || '—'}</span>
                    </td>
                    <td>
                      <div className="demosTwoLine">
                        <span className="demosPrimaryText">{demo.email || '—'}</span>
                        <span className="demosMutedText">{demo.phoneNumber || 'N/A'}</span>
                      </div>
                    </td>
                    <td>
                      <span className="demosPrimaryText">{demo.companyName || 'N/A'}</span>
                    </td>
                    <td>
                      <span className={getStatusClass(demo.status)}>{status.label}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="demoActions">
                        {demo.status === 'scheduled' ? (
                          <>
                            <button
                              type="button"
                              className="demoBtn demoBtnSuccess"
                              onClick={() => handleStatusChange(demo.id, 'completed')}
                            >
                              Complete
                            </button>
                            <button
                              type="button"
                              className="demoBtn demoBtnWarn"
                              onClick={() => handleStatusChange(demo.id, 'no-show')}
                            >
                              No show
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="demoBtn demoBtnDanger"
                          onClick={() => handleDeleteDemo(demo.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="demosCard" style={{ borderRadius: 0, boxShadow: 'none', border: 'none', padding: 16 }}>
          <div className="demosFooter">
            <div className="demosFooterLeft">
              <span>
                Showing <strong>{total === 0 ? 0 : startIndex + 1}</strong>–<strong>{endIndex}</strong> of{' '}
                <strong>{total}</strong>
              </span>
              <span>
                Rows:
                <select
                  className="demosSelect"
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(e.target.value)}
                  aria-label="Rows per page"
                  style={{ marginLeft: 8 }}
                >
                  {[5, 10, 25].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </span>
            </div>

            <div className="demosPager" aria-label="Pagination">
              <button type="button" className="demosPagerBtn" onClick={onPrev} disabled={!canPrev}>
                Prev
              </button>
              <span className="demosPill" aria-label="Current page">
                Page {Math.min(page + 1, pageCount)} / {pageCount}
              </span>
              <button type="button" className="demosPagerBtn" onClick={onNext} disabled={!canNext}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demos; 